<template>
  <!-- Design-system look: the one place for transient messages (notify() in composables/toast).
       Bottom centre, where no page has an action, so a message never covers the button the user
       wants to press next. Below 600px the toaster runs full width with 16px gutters and is swiped
       down to dismiss. -->
  <Toaster
    class="app-toaster"
    position="bottom-center"
    :offset="{ bottom: '20px' }"
    :mobile-offset="16"
    :visible-toasts="3"
    :gap="8"
    expand
    close-button
    :container-aria-label="$t('common.notifications')"
    :toast-options="{ closeButtonAriaLabel: $t('common.close') }"
    :style="{ '--width': TOAST_WIDTH }"
    data-test="app-toaster"
  >
    <!-- Plain glyphs, not FontAwesome: the design-system look swaps FontAwesome for its own icons,
         which bring a circle of their own and would sit off-centre in the coloured dot. -->
    <template #success-icon>
      <span class="app-toast-icon app-toast-icon--success" aria-hidden="true">
        <svg viewBox="0 0 12 12"><path d="M2.5 6.25 5 8.75l4.5-5" /></svg>
      </span>
    </template>
    <template #info-icon>
      <span class="app-toast-icon app-toast-icon--info" aria-hidden="true">
        <svg viewBox="0 0 12 12"><path d="M6 5.5v3.5M6 3h.01" /></svg>
      </span>
    </template>
    <template #error-icon>
      <span class="app-toast-icon app-toast-icon--error" aria-hidden="true">
        <svg viewBox="0 0 12 12"><path d="M6 2.75v3.75M6 9h.01" /></svg>
      </span>
    </template>
    <template #close-icon>
      <FontAwesomeIcon :icon="['fal', 'times']" aria-hidden="true" />
    </template>
  </Toaster>
</template>

<script setup>
import { Toaster } from 'vue-sonner'
import 'vue-sonner/style.css'

/** The toaster sets its width inline, so it is overridden here, not in the stylesheet. */
const TOAST_WIDTH = '420px'
</script>

<style>
/* Over sonner's own theme variables ([data-sonner-toaster][data-sonner-theme='light']). */
[data-sonner-toaster][data-sonner-theme].app-toaster {
  --border-radius: var(--ds-radius-md);
  --normal-bg: var(--ds-color-grey-900);
  --normal-border: var(--ds-color-grey-900);
  --normal-text: var(--ds-color-white);
  z-index: 99;
}

.app-toaster [data-sonner-toast][data-styled='true'] {
  gap: 12px;
  padding: 10px 40px 10px 14px;
  font-size: var(--ds-font-size-sm);
  box-shadow: 0 10px 30px rgb(20 18 30 / 0.28);
}

.app-toaster [data-sonner-toast][data-styled='true'] [data-icon] {
  width: 22px;
  height: 22px;
  flex: none;
}

.app-toast-icon {
  display: grid;
  place-items: center;
  width: 22px;
  height: 22px;
  border-radius: 9999px;
}

.app-toast-icon svg {
  width: 15px;
  height: 15px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.25;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.app-toast-icon--success {
  background: var(--ds-color-green-500);
  color: var(--ds-color-grey-900);
}

.app-toast-icon--info {
  background: var(--ds-color-violet-300);
  color: var(--ds-color-grey-900);
}

.app-toast-icon--error {
  background: var(--ds-color-red-500);
  color: var(--ds-color-white);
}

.app-toaster [data-sonner-toast][data-styled='true'] [data-title] {
  font-weight: var(--ds-font-weight-medium);
}

.app-toaster [data-sonner-toast][data-styled='true'] [data-description] {
  color: var(--ds-color-grey-300);
}

.app-toaster [data-sonner-toast][data-styled='true'] [data-button] {
  margin-left: auto;
  height: 28px;
  padding: 0 8px;
  background: transparent;
  color: var(--ds-color-violet-300);
  font-size: var(--ds-font-size-sm);
  font-weight: var(--ds-font-weight-bold);
  white-space: nowrap;
}

.app-toaster [data-sonner-toast][data-styled='true'] [data-button]:hover {
  background: rgb(255 255 255 / 0.08);
}

/* The close button sits inside the toast on its right edge, not on its corner. */
.app-toaster [data-sonner-toast][data-styled='true'] [data-close-button] {
  left: auto;
  right: 10px;
  top: 50%;
  bottom: auto;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: 0;
  background: transparent;
  color: var(--ds-color-grey-300);
}

.app-toaster [data-sonner-toast][data-styled='true'] [data-close-button]:hover {
  background: rgb(255 255 255 / 0.08);
  color: var(--ds-color-white);
}

.app-toaster [data-sonner-toast] :focus-visible {
  outline: 2px solid var(--ds-color-violet-300);
  outline-offset: 2px;
}
</style>
